import { Link, usePathname, type Href } from "expo-router";
import type { PropsWithChildren } from "react";
import { Pressable, ScrollView, StyleSheet, useWindowDimensions, View } from "react-native";

import { Text } from "@/src/components/ui/BrandText";
import { border, radius, spacing, surface, text } from "@/src/theme/tokens";
import { useControlAccessContext } from "./ControlAccessContext";
import { controlNavigationMode } from "./layout";
import { hasControlPermission, type ControlPermission } from "./permissions";
import { controlRoleLabel } from "./presentation";

const links: { href: string; label: string; permission?: ControlPermission; nested?: boolean }[] = [
  { href: "/control" as const, label: "Dashboard", permission: "control.dashboard.read" },
  { href: "/control/identity" as const, label: "Demandes d’identité", permission: "identity.requests.read", nested: true },
  { href: "/control/moderation" as const, label: "Modération", permission: "moderation.read", nested: true },
  { href: "/control/monetization" as const, label: "Campagnes", permission: "monetization.read", nested: true },
  { href: "/control/members" as const, label: "Membres Control", permission: "control.members.read" },
  { href: "/control/audit" as const, label: "Journal d’activité", permission: "control.audit.read" },
];

export function ControlShell({ children }: PropsWithChildren) {
  const { width } = useWindowDimensions();
  const desktop = controlNavigationMode(width) === "desktop";
  const pathname = usePathname();
  const access = useControlAccessContext();
  const visibleLinks = links.filter((link) =>
    !link.permission || (access.status === "allowed" && hasControlPermission(access.member, link.permission))
  );
  return (
    <View style={[styles.shell, desktop && styles.shellDesktop]}>
      <View style={[styles.navigation, desktop ? styles.sidebar : styles.topbar]}>
        <View style={styles.brandBlock}>
          <Text style={styles.brand}>NEQRAW</Text>
          <Text style={styles.control}>Control Center</Text>
        </View>
        <ScrollView
          horizontal={!desktop}
          style={!desktop && styles.compactLinks}
          contentContainerStyle={[styles.links, desktop && styles.linksDesktop]}
          showsHorizontalScrollIndicator={false}
        >
          {visibleLinks.map((link) => {
            const active = link.nested ? pathname.startsWith(link.href) : pathname === link.href;
            return (
              <Link href={link.href as Href} asChild key={link.href}>
                <Pressable accessibilityRole="link" style={StyleSheet.flatten([styles.link, active && styles.linkActive])}>
                  <Text style={[styles.linkText, active && styles.linkTextActive]}>{link.label}</Text>
                </Pressable>
              </Link>
            );
          })}
        </ScrollView>
        {access.status === "allowed" ? (
          <View style={styles.identity}>
            <Text style={styles.identityLabel}>Connecté en tant que</Text>
            <Text style={styles.identityName}>{access.member.displayName || access.member.email || "Membre Control"}</Text>
            <Text style={styles.identityRole}>{controlRoleLabel(access.member.role)}</Text>
          </View>
        ) : null}
      </View>
      <View style={styles.main}>{children}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  shell: { backgroundColor: surface.secondary, flex: 1, minWidth: 0 },
  shellDesktop: { flexDirection: "row" },
  navigation: { backgroundColor: surface.primary, borderColor: border.subtle, flexShrink: 0 },
  sidebar: { borderRightWidth: 1, minHeight: "100%", padding: spacing.xl, width: 240 },
  topbar: { borderBottomWidth: 1, gap: spacing.md, paddingHorizontal: spacing.lg, paddingTop: spacing.lg },
  compactLinks: { alignSelf: "stretch", flexGrow: 0, maxWidth: "100%" },
  brandBlock: { gap: 2 },
  brand: { color: text.brand, fontSize: 13, fontWeight: "800", letterSpacing: 2 },
  control: { color: text.primary, fontSize: 18, fontWeight: "800" },
  links: { gap: spacing.sm, paddingVertical: spacing.md },
  linksDesktop: { flexDirection: "column", flexGrow: 1, paddingTop: spacing.xl },
  link: { borderRadius: radius.md, minWidth: 104, paddingHorizontal: spacing.md, paddingVertical: 11 },
  linkActive: { backgroundColor: surface.selected },
  linkText: { color: text.secondary, fontWeight: "600" },
  linkTextActive: { color: text.brand },
  identity: { borderColor: border.subtle, borderRadius: radius.md, borderWidth: 1, gap: spacing.xs, marginTop: spacing.lg, padding: spacing.md },
  identityLabel: { color: text.muted, fontSize: 11 },
  identityName: { color: text.primary, fontSize: 13, fontWeight: "700" },
  identityRole: { color: text.brand, fontSize: 12, fontWeight: "700" },
  main: { flex: 1, minWidth: 0, overflow: "hidden" },
});
